// テーブルからバッチファイルを生成するハンドラー

import { generateBatchCode } from '../components/code-generator.js';
import { createBatchFileBlob } from '../utils/encoding.js';
import { getImportedApps } from '../state/app-state.js';
import { getSettings } from '../state/app-state.js';
import type { PCRow } from './table-handlers.js';
import { loadTableData } from './table-handlers.js';
import { saveVariableValue } from '../state/app-state.js';
import { saveSelectionState } from '../state/app-state.js';
import { t } from '../utils/i18n.js';

/**
 * テーブルデータから複数のバッチファイルを生成してダウンロード
 */
export function generateBatchFilesFromTable(): void {
    const tableData = loadTableData();
    const settings = getSettings();
    
    if (!settings) {
        alert(t('tableBatchSettingsNotLoaded'));
        return;
    }
    
    if (tableData.rows.length === 0) {
        alert(t('tableBatchNoPcs'));
        return;
    }
    
    // 各行（PC）からバッチファイルを生成
    const files: Array<{ name: string; code: string }> = [];
    
    tableData.rows.forEach((row, index) => {
        // 一時的に選択状態と変数の値を設定
        const originalSelectedIds = saveAndSetSelectionState(row);
        const originalVariables = saveAndSetVariableValues(row);
        
        try {
            // バッチコードを生成
            const batchCode = generateBatchCode(getImportedApps(), settings);
            
            // ファイル名を生成（PC名を使用）
            let fileName = `setup_${index + 1}.bat`;
            if (row.pcName && row.pcName.trim()) {
                fileName = `setup_${sanitizeFileName(row.pcName)}.bat`;
            }
            
            files.push({ name: fileName, code: batchCode });
        } finally {
            // 元の状態を復元
            restoreSelectionState(originalSelectedIds);
            restoreVariableValues(originalVariables);
        }
    });
    
    // ファイルをダウンロード
    if (files.length === 1) {
        // 1ファイルの場合は直接ダウンロード
        const file = files[0];
        if (!file) return;
        downloadSingleFile(file.name, file.code);
    } else {
        // 複数ファイルの場合は確認してから個別にダウンロード
        if (confirm(t('tableBatchConfirmMultiDownload', { count: files.length }))) {
            downloadFilesSequentially(files);
        }
    }
}

/**
 * 選択状態を保存して、行のデータに基づいて設定
 */
function saveAndSetSelectionState(row: PCRow): string[] {
    // 現在の選択状態を保存
    const currentSettings = getSettings();
    if (!currentSettings || !currentSettings.sections) return [];
    
    const originalSelectedIds: string[] = [];
    currentSettings.sections.forEach(section => {
        section.items.forEach(item => {
            const checkbox = document.getElementById(item.id) as HTMLInputElement | null;
            if (checkbox && checkbox.checked) {
                originalSelectedIds.push(item.id);
            }
        });
    });
    
    // 行のチェックボックス状態を反映
    const selectedIds: string[] = [];
    Object.keys(row.checkboxes).forEach(itemId => {
        if (row.checkboxes[itemId]) {
            selectedIds.push(itemId);
        }
    });
    
    // チェックボックスを更新
    currentSettings.sections.forEach(section => {
        section.items.forEach(item => {
            const checkbox = document.getElementById(item.id) as HTMLInputElement | null;
            if (checkbox) {
                checkbox.checked = selectedIds.includes(item.id);
            }
        });
    });
    
    // 選択状態を保存
    saveSelectionState(selectedIds);
    
    return originalSelectedIds;
}

/**
 * 変数の値を保存して、行のデータに基づいて設定
 */
function saveAndSetVariableValues(row: PCRow): Record<string, Record<string, string>> {
    // 現在の変数値を保存
    const originalVariables: Record<string, Record<string, string>> = {};
    
    if (typeof window !== 'undefined' && window.localStorage) {
        try {
            const raw = window.localStorage.getItem('winbatchgen_variables_v1');
            if (raw) {
                Object.assign(originalVariables, JSON.parse(raw));
            }
        } catch (e) {
            console.warn('変数値の保存に失敗しました:', e);
        }
    }
    
    // 行の変数値を設定
    Object.keys(row.variables).forEach(itemId => {
        const itemVars = row.variables[itemId];
        if (!itemVars) return;
        Object.keys(itemVars).forEach(varName => {
            const value = itemVars[varName] ?? '';
            saveVariableValue(itemId, varName, value);
            
            // UIの入力欄も更新
            const input = document.querySelector(`input[data-item-id="${itemId}"][data-var-name="${varName}"]`) as HTMLInputElement | null;
            if (input) {
                input.value = value;
            }
        });
    });
    
    return originalVariables;
}

/**
 * 選択状態を復元
 */
function restoreSelectionState(originalSelectedIds: string[]): void {
    const currentSettings = getSettings();
    if (!currentSettings || !currentSettings.sections) return;
    
    // チェックボックスを元の状態に戻す
    currentSettings.sections.forEach(section => {
        section.items.forEach(item => {
            const checkbox = document.getElementById(item.id) as HTMLInputElement | null;
            if (checkbox) {
                checkbox.checked = originalSelectedIds.includes(item.id);
            }
        });
    });
    
    // 選択状態を保存
    saveSelectionState(originalSelectedIds);
}

/**
 * 変数の値を復元
 */
function restoreVariableValues(originalVariables: Record<string, Record<string, string>>): void {
    Object.keys(originalVariables).forEach(itemId => {
        const itemVars = originalVariables[itemId];
        if (!itemVars) return;
        Object.keys(itemVars).forEach(varName => {
            const value = itemVars[varName] ?? '';
            saveVariableValue(itemId, varName, value);
            
            // UIの入力欄も更新
            const input = document.querySelector(`input[data-item-id="${itemId}"][data-var-name="${varName}"]`) as HTMLInputElement | null;
            if (input) {
                input.value = value;
            }
        });
    });
}

/**
 * ファイル名をサニタイズ（Windowsで使えない文字を置換）
 */
function sanitizeFileName(name: string): string {
    return name
        .replace(/[<>:"/\\|?*]/g, '_')
        .replace(/\s+/g, '_')
        .substring(0, 50); // 長すぎる場合は切り詰め
}

/**
 * 単一ファイルをダウンロード
 */
function downloadSingleFile(fileName: string, code: string): void {
    const blob = createBatchFileBlob(code);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

/**
 * 複数ファイルを順番にダウンロード
 */
function downloadFilesSequentially(files: Array<{ name: string; code: string }>): void {
    let index = 0;
    
    const downloadNext = () => {
        if (index >= files.length) {
            alert(t('tableBatchDownloadComplete'));
            return;
        }
        
        const file = files[index];
        if (!file) return;
        downloadSingleFile(file.name, file.code);
        index++;
        
        // 少し遅延させてから次のファイルをダウンロード（ブラウザの制限を回避）
        setTimeout(downloadNext, 500);
    };
    
    downloadNext();
}
